// Core packages
import { formatDate } from "@angular/common";
import {
  AfterViewInit, Component, OnDestroy, OnInit, ViewChild
} from "@angular/core";
import { FormControl } from "@angular/forms";

// Third party packages
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { combineLatest, merge, Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  startWith, tap
} from "rxjs/operators";

// Core packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { UsersService } from "src/app/modules/users/users.service";
import { ListSetting } from "src/app/shared/interfaces/list-setting.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { UsersDataSource } from "src/app/shared/utils/data-sources/users-data-source";

@Component({
  selector: "app-users-list",
  templateUrl: "./users-list.page.html",
  styleUrls: ["./users-list.page.scss"],
})
export class UsersListComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  // BEGIN - Table properties
  displayedColumns: string[] = [
    "index",
    "firstName",
    "lastName",
    "email",
    "role",
    "createdAt",
    "actions",
  ];
  dataSource: UsersDataSource;
  @ViewChild("sort1", { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild("input", { static: true }) input: ElementRef;
  firstNameFilter: FormControl = new FormControl("");
  lastNameFilter: FormControl = new FormControl("");
  emailFilter: FormControl = new FormControl("");
  roleFilter: FormControl = new FormControl("");
  createdAtFilter: FormControl = new FormControl("");
  // END - Table properties

  activeRoute: string;
  availableRoles: any[] = [];
  loggedUserRoleType = "";
  loggedUserCompanyId = "";

  /**
   * Class constructor
   */
  constructor(
    private usersService: UsersService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private configService: ConfigService
  ) {}

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("users.list.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );
    this.subscriptions.push(
      this.authService.loggedUserRequestedRoleType$.subscribe((res) => {
        this.loggedUserRoleType = res;
        if (res === "admin" || res === "operator") {
          this.availableRoles = ["admin", "operator"];
        }
        if (res === "master" || res === "collaborator") {
          this.availableRoles = ["master", "collaborator"];
          this.subscriptions.push(
            this.authService.loggedUserRequestedRoleObj$.subscribe(
              (roleObj) => {
                if (roleObj && roleObj.company) {
                  this.loggedUserCompanyId = roleObj.company._id;
                }
              }
            )
          );
        }
      })
    );

    // Init data source
    this.dataSource = new UsersDataSource(
      this.usersService,
      this.helperService
    );

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings("users");
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0))
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadPage()))
        .subscribe()
    );

    // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.input.nativeElement, "keyup")
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.paginator.pageIndex = 0; // Reset page
    //         this.loadPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.firstNameFilter.valueChanges.pipe(startWith(null as string)),
        this.lastNameFilter.valueChanges.pipe(startWith(null as string)),
        this.emailFilter.valueChanges.pipe(startWith(null as string)),
        this.roleFilter.valueChanges.pipe(startWith(null as string)),
        this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged() // Eliminate duplicate values
        )
        .subscribe((res) => {
          // Load page only if at least one value is not null
          if (!res.every((value) => value === null)) {
            this.paginator.pageIndex = 0; // Reset to first page on filter change
            this.loadPage();
          }
        })
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      "users",
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        general: "", // this.input.nativeElement.value,
        firstName: this.firstNameFilter.value,
        lastName: this.lastNameFilter.value,
        email: this.emailFilter.value,
        role: this.roleFilter.value,
        createdAt: this.createdAtFilter.value,
      }
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search: Record<string, any> = {
      general: "", // this.input.nativeElement.value,
      firstName: this.firstNameFilter.value,
      lastName: this.lastNameFilter.value,
      email: this.emailFilter.value,
      role: this.roleFilter.value,
      createdAt: this.createdAtFilter.value
        ? new Date(this.createdAtFilter.value).toISOString()
        : undefined,
    };
    this.dataSource.loadItems(
      start,
      length,
      this.sort.active,
      this.sort.direction,
      search
    );
  }

  /**
   * Export users in a excel file.
   * The users exported depend on who has send export request:
   * Admin and Operator can export Admin and Operator users;
   * Master and Collaborator can export the company users, like
   * Master, Operator and Temporary.
   *
   * @since 1.0.0
   */
  onExport(): void {
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "users.list.exportFileName"
    )} ${date}`;
    this.subscriptions.push(
      this.usersService
        .getExport(this.loggedUserRoleType, this.loggedUserCompanyId, "all")
        .subscribe(
          (res) => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel",
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          (err) => {
            this.helperService.handleError(err);
          }
        )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting("", 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = "";
    this.firstNameFilter.setValue(null);
    this.lastNameFilter.setValue(null);
    this.emailFilter.setValue(null);
    this.roleFilter.setValue(null);
    this.createdAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "desc";
    this.sort.active = "createdAt";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.paginator.pageIndex = 0;
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe((settings: ListSetting[]) => {
        if (settings.length) {
          settings.forEach((setting: ListSetting) => {
            if (setting.name === name) {
              if (setting.pageIndex) {
                this.paginator.pageIndex = setting.pageIndex;
              }
              if (setting.pageSize) {
                this.paginator.pageSize = setting.pageSize;
              }
              if (setting.search && setting.search.general) {
                // this.input.nativeElement.value = setting.search.general;
              }
              if (setting.search && setting.search.firstName) {
                this.firstNameFilter.setValue(setting.search.firstName);
              }
              if (setting.search && setting.search.lastName) {
                this.lastNameFilter.setValue(setting.search.lastName);
              }
              if (setting.search && setting.search.email) {
                this.emailFilter.setValue(setting.search.email);
              }
              if (setting.search && setting.search.role) {
                this.roleFilter.setValue(setting.search.role);
              }
              if (setting.search && setting.search.createdAt) {
                this.createdAtFilter.setValue(setting.search.createdAt);
              }
            }
          });
        }
      })
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    // this.input.nativeElement.dispatchEvent(event);
  }
}
